import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// all Business Sector (optional query params for list filters)
export const fetchBusinessSectors = createAsyncThunk(
  "businessSector/fetchAll",
  async (arg, { rejectWithValue }) => {
    const params =
      arg && typeof arg === "object" && !Array.isArray(arg) ? arg : {};
    try {
      const response = await axios.get(`${BASE_URL}/business-sectors`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
        params,
      });
      return response.data?.data ?? { data: [], pagination: null };
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

// add businessSector
export const addBusinessSector = createAsyncThunk(
  "businessSector/add",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/business-sectors/store`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data ?? { message: "An unknown error occurred" }
      );
    }
  }
);
// get businessSector record
export const businessSectorRecord = createAsyncThunk(
  "businessSector/businessSectorRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/business-sectors/show/${id}`);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue({ message });
    }
  }
);
// update businessSector
export const updateBusinessSector = createAsyncThunk(
  "businessSector/updateBusinessSector",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && formData instanceof FormData;
      if (isFormData) {
        if (!formData.has("business_sector_id")) {
          formData.append("business_sector_id", String(id));
        }
        const response = await axios.post(
          `${BASE_URL}/business-sectors/update`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              Lang: i18n.language,
            },
          }
        );
        return response.data;
      }
      const response = await axios.post(
        `${BASE_URL}/business-sectors/update`,
        { business_sector_id: id, ...formData },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);
// delete businessSector
export const deleteBusinessSector = createAsyncThunk(
  "businessSector/deleteBusinessSector",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/business-sectors/delete`,
        { business_sector_id: id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete faq";
      return rejectWithValue({ message });
    }
  }
);
const BusinessSectorSlice = createSlice({
  name: "businessSector",
  initialState: {
    businessSector: [],
    pagination: null,
    record: null,
    isLoading: false,
    error: null,
    success: null,
    lastCreatedId: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.lastCreatedId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessSectors.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBusinessSectors.fulfilled, (state, action) => {
        state.isLoading = false;
        const p = action.payload;
        if (Array.isArray(p)) {
          state.businessSector = p;
          state.pagination = null;
          return;
        }
        const rows = Array.isArray(p?.data) ? p.data : [];
        state.businessSector = rows;
        state.pagination = p?.pagination ?? null;
      })
      .addCase(fetchBusinessSectors.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(addBusinessSector.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastCreatedId = null;
      })
      .addCase(addBusinessSector.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || true;
        const payloadData = action.payload?.data;
        state.lastCreatedId =
          payloadData?.id ??
          payloadData?.business_sector_id ??
          (typeof payloadData === "number" || typeof payloadData === "string"
            ? payloadData
            : null) ??
          action.payload?.id ??
          null;
      })
      .addCase(addBusinessSector.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(businessSectorRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
        state.record = null;
      })
      .addCase(businessSectorRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload?.data ?? null;
      })
      .addCase(businessSectorRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.record = null;
        state.error =
          action.payload?.message ||
          action.error?.message ||
          "Failed to load data";
      })
      .addCase(updateBusinessSector.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBusinessSector.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updateBusinessSector.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          action.payload ||
          "Failed to update record";
      })
      .addCase(deleteBusinessSector.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteBusinessSector.fulfilled, (state, action) => {
        state.isLoading = false;

        if (Array.isArray(state.businessSector)) {
          state.businessSector = state.businessSector.filter(
            (record) => record.id !== action.meta.arg
          );
        }

        state.success = action.payload?.message || "Deleted successfully";
      })
      .addCase(deleteBusinessSector.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          action.payload ||
          action.error?.message ||
          "Failed to delete business sector";
      });
  },
});

export const { clearState } = BusinessSectorSlice.actions;
export default BusinessSectorSlice.reducer;
